/* =========================================================
   NeuroPulse LP v2 — base runtime
   Converted from React hooks (useReveal, Countdown, etc.)
   to vanilla ES6+. Scoped to .lp-v2.
   Utilities added as sections are built.
   ========================================================= */
(function () {
  'use strict';

  const root = document.querySelector('.lp-v2');
  if (!root) return;

  // ------------------------------------------------------------
  // Reveal on scroll (replacement for useReveal hook)
  // ------------------------------------------------------------
  function initReveal() {
    const targets = root.querySelectorAll(
      '.reveal, .reveal-left, .reveal-right, .reveal-scale'
    );
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
          } else {
            entry.target.classList.remove('is-in');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );

    targets.forEach((el) => io.observe(el));
  }

  // ------------------------------------------------------------
  // Smooth scroll to #lp-pricing (and other internal anchors)
  // ------------------------------------------------------------
  function initAnchorScroll() {
    root.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const id = link.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      const offset = 120;
      const top =
        target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  // ------------------------------------------------------------
  // Countdown helper — supports two modes:
  //   data-countdown-mode="endofday" → counts to local 23:59:59 today (HMS)
  //   default                        → 5d 9h 25m 54s from first load (DHMS)
  // Each countdown node owns its own target via data attributes.
  // ------------------------------------------------------------
  function getEndOfDayTarget() {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  }

  function getRollingTarget() {
    // Match LP1 behavior: reset on every page load (no sessionStorage cache)
    const ms =
      5 * 24 * 60 * 60 * 1000 +
      9 * 60 * 60 * 1000 +
      25 * 60 * 1000 +
      54 * 1000;
    return Date.now() + ms;
  }

  function initCountdowns() {
    const nodes = root.querySelectorAll('[data-countdown]');
    if (!nodes.length) return;

    const targets = new Map();
    nodes.forEach((node) => {
      const mode = node.getAttribute('data-countdown-mode') || 'rolling';
      targets.set(
        node,
        mode === 'endofday' ? getEndOfDayTarget() : getRollingTarget()
      );
    });

    function render() {
      nodes.forEach((node) => {
        const target = targets.get(node);
        const diff = Math.max(0, target - Date.now());
        const d = Math.floor(diff / (24 * 60 * 60 * 1000));
        const h = Math.floor((diff / (60 * 60 * 1000)) % 24);
        const m = Math.floor((diff / (60 * 1000)) % 60);
        const s = Math.floor((diff / 1000) % 60);
        const dEl = node.querySelector('[data-cd-d], [data-days]');
        const hEl = node.querySelector('[data-cd-h], [data-hours]');
        const mEl = node.querySelector('[data-cd-m], [data-mins]');
        const sEl = node.querySelector('[data-cd-s], [data-secs]');
        if (dEl) dEl.textContent = d;
        if (hEl) {
          const isEod = node.getAttribute('data-countdown-mode') === 'endofday';
          const totalH = isEod || dEl ? h : d * 24 + h;
          hEl.textContent = isEod || dEl ? totalH : String(totalH).padStart(2, '0');
        }
        if (mEl) mEl.textContent = String(m).padStart(2, '0');
        if (sEl) sEl.textContent = String(s).padStart(2, '0');
      });
    }
    render();
    setInterval(render, 1000);
  }

  // ------------------------------------------------------------
  // GlowyWaves — animated radial gradient (ported from waves.jsx)
  // ------------------------------------------------------------
  const GLOWY_PRESETS = {
    pulse:   { startingGap: 125, breathing: true, breathingRange: 8,  animationSpeed: 0.015, colors: ['#070B14','#0B1120','#0B5394','#3B82E8','#A8C5EC','#E8EEF7'], stops: [35,50,65,78,90,100], topOffset: 0 },
    prism:   { startingGap: 120, breathing: true, breathingRange: 6,  animationSpeed: 0.012, colors: ['#070B14','#0B1120','#12295A','#0B5394','#6FA4E0','#DCE7F3'], stops: [30,48,62,76,88,100], topOffset: 10 },
    ember:   { startingGap: 130, breathing: true, breathingRange: 10, animationSpeed: 0.018, colors: ['#070B14','#0B1120','#0B5394','#4A90E2','#8FB8E8','#EEF3FA'], stops: [32,50,66,80,92,100], topOffset: -10 },
    stripes: { startingGap: 115, breathing: true, breathingRange: 5,  animationSpeed: 0.010, colors: ['#070B14','#0B1120','#1D3A6B','#3B6BA8','#7A9BBC','#D6E1EE'], stops: [30,48,62,76,88,100], topOffset: 20 },
    void:    { startingGap: 140, breathing: true, breathingRange: 4,  animationSpeed: 0.008, colors: ['#050810','#070B14','#0B1120','#12295A','#1D3A6B','#3B6BA8'], stops: [25,45,62,78,90,100], topOffset: 30 },
  };

  function initGlowyWaves() {
    const wraps = root.querySelectorAll('.aurora-bg');
    if (!wraps.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    wraps.forEach((wrap) => {
      const presetName = wrap.getAttribute('data-preset') || 'pulse';
      const intensity = parseFloat(wrap.getAttribute('data-intensity') || '1');
      const p = GLOWY_PRESETS[presetName] || GLOWY_PRESETS.pulse;
      const radial = wrap.querySelector('.aurora-radial');
      if (!radial) return;

      // Set fallback bg + initial gradient
      wrap.style.background = p.colors[0];
      const stopsStr = p.stops
        .map((stop, i) => `${p.colors[i]} ${stop}%`)
        .join(', ');

      let width = p.startingGap;
      let dir = 1;
      const speed = p.animationSpeed * intensity;

      const apply = () => {
        const gradient = `radial-gradient(${width}% ${width + p.topOffset}% at 50% 20%, ${stopsStr})`;
        radial.style.background = gradient;
      };
      apply();

      // entrance fade + scale
      requestAnimationFrame(() => wrap.classList.add('is-on'));

      if (reduced || !p.breathing) return;

      function tick() {
        if (width >= p.startingGap + p.breathingRange) dir = -1;
        if (width <= p.startingGap - p.breathingRange) dir = 1;
        width += dir * speed;
        apply();
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    });
  }

  // ------------------------------------------------------------
  // FAQ accordion + search
  // ------------------------------------------------------------
  function initFAQ() {
    const list = root.querySelector('[data-faq-list]');
    if (!list) return;

    list.addEventListener('click', (e) => {
      const btn = e.target.closest('.faq-q');
      if (!btn) return;
      const item = btn.closest('.faq-item');
      if (!item) return;
      const isOpen = item.classList.toggle('open');
      btn.setAttribute('aria-expanded', String(isOpen));
      const ans = item.querySelector('.faq-a');
      if (ans) ans.style.maxHeight = isOpen ? '800px' : '0';
    });

    const search = root.querySelector('[data-faq-search]');
    if (!search) return;
    search.addEventListener('input', (e) => {
      const q = e.target.value.toLowerCase().trim();
      list.querySelectorAll('[data-faq-item]').forEach((item) => {
        const text = item.textContent.toLowerCase();
        item.style.display = !q || text.includes(q) ? '' : 'none';
      });
    });
  }

  // ------------------------------------------------------------
  // Mobile burger menu
  // ------------------------------------------------------------
  function initMobileMenu() {
    const burger = root.querySelector('[data-burger]');
    const menu = root.querySelector('[data-mobile-menu]');
    const close = root.querySelector('[data-close-menu]');
    if (!burger || !menu) return;

    function open() {
      menu.classList.add('open');
      menu.setAttribute('aria-hidden', 'false');
      burger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function shut() {
      menu.classList.remove('open');
      menu.setAttribute('aria-hidden', 'true');
      burger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
    burger.addEventListener('click', () => {
      menu.classList.contains('open') ? shut() : open();
    });
    if (close) close.addEventListener('click', shut);
    menu.querySelectorAll('a').forEach((a) => a.addEventListener('click', shut));
  }

  // ------------------------------------------------------------
  // Pricing tier toggle (qty-toggle)
  // ------------------------------------------------------------
  function initTierToggle() {
    const toggles = root.querySelectorAll('[data-tier-toggle]');
    const tiers = root.querySelectorAll('[data-tier]');
    if (!toggles.length || !tiers.length) return;

    toggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.getAttribute('data-tier-toggle');
        toggles.forEach((b) => {
          const active = b.getAttribute('data-tier-toggle') === target;
          b.classList.toggle('active', active);
          b.setAttribute('aria-selected', String(active));
        });
        tiers.forEach((t) => {
          t.classList.toggle('is-selected', t.getAttribute('data-tier') === target);
        });
      });
    });
  }

  // ------------------------------------------------------------
  // Sticky CTA — show after scrolling past hero
  // ------------------------------------------------------------
  function initStickyCTA() {
    const sticky = root.querySelector('[data-sticky-cta]');
    if (!sticky) return;
    const trigger = root.querySelector('.hero');
    if (!trigger) {
      sticky.classList.add('show');
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          sticky.classList.toggle('show', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    io.observe(trigger);
  }

  // ------------------------------------------------------------
  // Nav — add .scrolled when scrolled
  // ------------------------------------------------------------
  function initNavScroll() {
    const nav = root.querySelector('.nav');
    if (!nav) return;
    function update() {
      nav.classList.toggle('scrolled', window.scrollY > 8);
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  // ------------------------------------------------------------
  // Hero carousel — dot tracking + click-to-scroll
  // ------------------------------------------------------------
  function initHeroCarousel() {
    const carousel = root.querySelector('[data-hero-carousel]');
    if (!carousel) return;
    const track = carousel.querySelector('[data-hero-carousel-track]');
    const dots = carousel.querySelectorAll('[data-hero-dot]');
    if (!track || !dots.length) return;

    function update() {
      const cards = track.children;
      if (!cards.length) return;
      const trackRect = track.getBoundingClientRect();
      let activeIdx = 0;
      let bestDelta = Infinity;
      for (let i = 0; i < cards.length; i++) {
        const cardRect = cards[i].getBoundingClientRect();
        const delta = Math.abs(cardRect.left - trackRect.left);
        if (delta < bestDelta) { bestDelta = delta; activeIdx = i; }
      }
      dots.forEach((d, i) => d.classList.toggle('is-active', i === activeIdx));
    }
    track.addEventListener('scroll', update, { passive: true });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const card = track.children[i];
        if (card) {
          track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        }
      });
    });
    update();
  }

  // ------------------------------------------------------------
  // Boot
  // ------------------------------------------------------------
  function boot() {
    initReveal();
    initAnchorScroll();
    initCountdowns();
    initGlowyWaves();
    initFAQ();
    initMobileMenu();
    initTierToggle();
    initStickyCTA();
    initNavScroll();
    initHeroCarousel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
